import { useCallback, useMemo, Suspense, useState, useEffect } from 'react';
import { lazily } from 'react-lazily';
import { findLastIndex, isEqual } from 'lodash-es';

import type { Site } from '@hotjar/site';
import { useCurrentSiteId } from '@hotjar/site';
import { getDefaultFilterInUiFormat } from '@hotjar/global-filters';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { noop } from '@hotjar/webapp/lib/function';
import { PageLoader } from '@hotjar/webapp/components/PageLoader/PageLoader';
import type { FunnelQuery, FunnelResult } from '@hotjar/dashboard-quantz';
import { ErrorBoundaryForPages } from '@hotjar/error-handler';
import { useGate } from '@hotjar/allowances';
import { TrafficAndCoverageBanner } from '@hotjar/webapp/components/TrafficAndCoverageBanner/TrafficAndCoverageBanner';
import { TRAFFIC_UPSELL_BANNER } from '@hotjar/upsell';
import { useAccountTrialInfo } from '@hotjar/account';
import { isOnActiveOrEligibleTrial } from '@hotjar/subscription-management';
import { useFeatureFlag } from '@hotjar/feature-flags';
import { QuerySection } from './QuerySection/QuerySection';
import { SplashScreen } from './SplashScreen';
import { trackFunnelViewed } from './trackMethods';
import { TrialGuideCard } from './GuideCard/TrialGuideCard';
import { FunnelsDateRange } from './QuerySection/FunnelsDateRange';

const shouldStopShowingReducedUi = (currentFunnelQuery: FunnelQuery): boolean => {
  if (currentFunnelQuery.steps.length > 2) {
    return true;
  }

  if (
    currentFunnelQuery.steps.length === 2 &&
    currentFunnelQuery.steps[0].stepInUiFormat.length > 0 &&
    currentFunnelQuery.steps[1].stepInUiFormat.length > 0
  ) {
    return true;
  }

  if (currentFunnelQuery.filterChainSeries.length > 1) {
    return true;
  }

  if (
    currentFunnelQuery.filterChainSeries.length === 1 &&
    currentFunnelQuery.filterChainSeries[0].filtersInUiFormat.length > 0
  ) {
    return true;
  }

  if (!isEqual(currentFunnelQuery.dateRange, getDefaultFilterInUiFormat().dateRange)) {
    return true;
  }

  return false;
};

// We use lazily lib to be able to import component without a default export.
const { FunnelsPage } = lazily(() => import('@hotjar/dashboard-quantz'));

interface FunnelsPageWithDataProps {
  site: Site;
  coveragePercentage: number;
  currentFunnelQuery: FunnelQuery;
  setCurrentFunnelQueryAndUrl: (newFunnelQuery: FunnelQuery) => void;
}

export const FunnelsPageWithData = ({
  site,
  coveragePercentage,
  currentFunnelQuery,
  setCurrentFunnelQueryAndUrl,
}: FunnelsPageWithDataProps): JSX.Element => {
  const currentSiteId = useCurrentSiteId();
  const { click, viewElement, getPermissionLevelForSite } = useTrack();
  const isFunnelFeatureAllowed = useGate('analytics.funnels_page', {
    id: currentSiteId,
    type: 'site',
  });
  /**
   * TODO: Refactor logic in RECS-1380 to be self-contained in hook
   */
  const trialInfo = useAccountTrialInfo();
  const isNewScaleEnabled = useFeatureFlag('billing_new_scale');
  const isSiteInScaleTrialCohort =
    useFeatureFlag('scale_trial_cohort') && site?.effectivePlan === 'business';
  const isScaleTrial =
    (isNewScaleEnabled && isSiteInScaleTrialCohort) ||
    (!isNewScaleEnabled &&
      !isOnActiveOrEligibleTrial(trialInfo) &&
      site?.effectivePlan === 'business');
  const [showReducedUi, setShowReducedUi] = useState<boolean>(true);

  const effectiveFunnelQuery = useMemo(() => {
    // We ignore the unsetted last steps
    const lastValidStepIndex = findLastIndex(
      currentFunnelQuery.steps,
      (item) => item.stepInUiFormat && item.stepInUiFormat.length > 0,
    );
    return {
      ...currentFunnelQuery,
      steps: currentFunnelQuery.steps.slice(0, lastValidStepIndex + 1),
    };
  }, [currentFunnelQuery]);

  const segmentData = useMemo(
    () => ({
      account_id: site.account.id,
      site_id: site.id,
      organization_id: site.organization.id,
      permission_level: getPermissionLevelForSite(site) || '',
      shown_traffic_coverage: coveragePercentage,
      state_event_was_triggered_on: 'funnels',
    }),
    [coveragePercentage, getPermissionLevelForSite, site],
  );

  const trackClick = useCallback(
    (eventName: string, uiPosition: string, additionalData: Record<string, string>) =>
      click(eventName, uiPosition, {
        ...segmentData,
        ...additionalData,
      }),
    [click, segmentData],
  );

  const trackViewElement = useCallback(
    (eventName: string, uiPosition: string, additionalData: Record<string, string>) =>
      viewElement(eventName, uiPosition, {
        ...segmentData,
        ...additionalData,
      }),
    [segmentData, viewElement],
  );

  const openUpsellModal = useCallback(
    (
      referrer: KnownReferrer,
      additionalTrackingData?: {
        // eslint-disable-next-line camelcase
        ui_position: string;
      },
    ) => {
      upsell({
        site,
        referrer,
        additionalTrackingData: {
          state_event_was_triggered_on: 'funnels',
          ...additionalTrackingData,
        },
      }).then(noop, noop);
    },
    [site],
  );

  const stepsWithError = useMemo(() => {
    const errorSteps: string[] = [];
    effectiveFunnelQuery.steps.forEach((series) => {
      if (effectiveFunnelQuery.steps.length > 2) {
        if (series.stepInUiFormat.length === 0) {
          errorSteps.push(series.key);
        }
      }
    });

    return errorSteps;
  }, [effectiveFunnelQuery.steps]);

  useEffect(() => {
    if (showReducedUi && shouldStopShowingReducedUi(currentFunnelQuery)) {
      // If the 2 first steps are defined or there are global filters or non default date range,
      // we show the full UI
      setShowReducedUi(false);
    }
  }, [showReducedUi, currentFunnelQuery]);

  const filterAndStepList = useMemo(() => {
    return [
      ...currentFunnelQuery.filterChainSeries.map((item) => item.filtersInUiFormat).flat(),
      // We need to pass the steps as well to handle properly date constraint
      // Example: if a click filter is used in a step, we should disable some date ranges.
      ...currentFunnelQuery.steps.map((item) => item.stepInUiFormat).flat(),
    ];
  }, [currentFunnelQuery.filterChainSeries, currentFunnelQuery.steps]);

  if (!isScaleTrial && !isFunnelFeatureAllowed && site.id !== 14) {
    return (
      <SplashScreen
        trackClick={(eventName, additionalData) => {
          // eslint-disable-next-line camelcase
          const { ui_position, ...rest } = additionalData;
          trackClick(eventName, ui_position, rest as Record<string, string>);
        }}
        trackViewElement={(eventName, additionalData) => {
          // eslint-disable-next-line camelcase
          const { ui_position, ...rest } = additionalData;
          trackViewElement(eventName, ui_position, rest as Record<string, string>);
        }}
      />
    );
  }

  const querySectionComponent = (
    <QuerySection
      currentFunnelQuery={currentFunnelQuery}
      stepsWithError={stepsWithError}
      setCurrentFunnelQuery={setCurrentFunnelQueryAndUrl}
      onUpsellRequest={openUpsellModal}
      trackClick={(eventName, additionalData) => {
        // eslint-disable-next-line camelcase
        const { ui_position, ...rest } = additionalData;
        trackClick(eventName, ui_position, rest as Record<string, string>);
      }}
      showReducedUi={showReducedUi}
    />
  );

  const dateRangeComponent = (
    <FunnelsDateRange
      currentFiltersInUiFormat={{
        dateRange: currentFunnelQuery.dateRange,
        filterList: filterAndStepList,
      }}
      onFiltersChange={(newFilterInUiFormat) => {
        setCurrentFunnelQueryAndUrl({
          ...currentFunnelQuery,
          dateRange: newFilterInUiFormat.dateRange ?? currentFunnelQuery.dateRange,
        });
      }}
      onUpsellRequest={openUpsellModal}
    />
  );

  const bannerComponent = (
    <TrafficAndCoverageBanner
      type={TRAFFIC_UPSELL_BANNER.FUNNELS}
      track={{ page: 'funnels', trackViewElement, trackClick }}
    />
  );

  return (
    <ErrorBoundaryForPages squadSentryTag="product-foundations" featureName="funnels">
      <Suspense fallback={<PageLoader />}>
        <FunnelsPage
          currentFunnelQuery={effectiveFunnelQuery}
          setChartType={(newChartType) => {
            setCurrentFunnelQueryAndUrl({
              ...currentFunnelQuery,
              chartType: newChartType,
            });
          }}
          querySectionComponent={querySectionComponent}
          dateRangeComponent={dateRangeComponent}
          bannerComponent={bannerComponent}
          siteId={site.id}
          openUpsellModal={
            (referrer, additionalTrackingData) =>
              openUpsellModal(referrer as KnownReferrer, additionalTrackingData) // FIXME: remove type assertion
          }
          trackFunnelViewed={(funnelsResultList?: FunnelResult[]) =>
            trackFunnelViewed(segmentData, effectiveFunnelQuery, funnelsResultList)
          }
          showReducedUi={showReducedUi}
          isScaleTrial={isScaleTrial}
          guideCard={isScaleTrial && <TrialGuideCard />}
        />
      </Suspense>
    </ErrorBoundaryForPages>
  );
};
